import {
  mutationOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { getSession } from '@/features/auth/session-store'
import type { ActionScopes } from './scopes'

export type UndoRedoResultCode = 'SUCCESS' | 'NOTHING_TO_DO' | 'SKIPPED_DUE_TO_ERROR'

export interface UndoRedoResult {
  result_code: UndoRedoResultCode
  actions: { action_type: string | null; action_scope: string | null }[]
}

/**
 * Undo and redo: the same request against two endpoints.
 *
 * The stack itself is the server's, keyed by the `ClientSessionId` header the
 * auth middleware already puts on every request. So there is no client state
 * here and nothing to keep in a store -- the plan counted undo/redo as one of
 * four Zustand slices, and it turns out to be a mutation.
 */
function undoRedoMutation(
  queryClient: QueryClient,
  path: '/api/user/undo/' | '/api/user/redo/',
) {
  return mutationOptions({
    mutationFn: (scopes: ActionScopes): Promise<UndoRedoResult> =>
      unwrap(
        getApi().PATCH(path, {
          // Named here as well as set by the auth middleware, from the same
          // place. Everywhere else it is incidental -- it groups a tab's actions
          // -- but here it *is* the request: it selects whose stack to walk, and
          // the schema marks it required for exactly that reason.
          params: { header: { ClientSessionId: getSession().clientSessionId } },
          body: { scopes },
        }),
      ) as Promise<UndoRedoResult>,

    onSuccess: async (result) => {
      if (result.result_code !== 'SUCCESS') return

      // Everything, because the response says *that* something changed and not
      // what. It names an action type and a scope, which upstream feeds to a
      // registry of per-action cache updaters -- a large surface to keep correct
      // for a path taken a handful of times in a session.
      //
      // The cost is easy to misjudge: `invalidateQueries` refetches only what
      // something is subscribed to, so this is a refetch of what is on screen,
      // not of the cache.
      //
      // It is also not optional. The undoing tab is excluded from its own
      // broadcasts -- that is what the `WebSocketId` header is for -- so no
      // realtime event is coming to describe what the undo did.
      await queryClient.invalidateQueries()
    },
  })
}

export function undoMutation(queryClient: QueryClient) {
  return undoRedoMutation(queryClient, '/api/user/undo/')
}

export function redoMutation(queryClient: QueryClient) {
  return undoRedoMutation(queryClient, '/api/user/redo/')
}

export function useUndo() {
  return useMutation(undoMutation(useQueryClient()))
}

export function useRedo() {
  return useMutation(redoMutation(useQueryClient()))
}
